import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Share2, Loader2 } from "lucide-react";
import { Button } from "./legacy-ui.js";
import { AppModalShell, AppModalFooter } from "./app-ui.js";
import InvoiceShareCard from "./InvoiceShareCard.js";
import { useInvoiceShareImage } from "../hooks/useInvoiceShareImage.js";
import { formatCurrency, formatDate, sellerLabel } from "../utils/formatters.js";
import { getPaymentMethods } from "../utils/api.js";
import {
  invoiceHasPendingBalance,
  resolvePaymentMethodName,
} from "../utils/paymentUtils.js";
import { COMPANY_NAME, COMPANY_RIF } from "../utils/company.js";
import BrandLogo from "./BrandLogo.js";

function filterCreditLogsForInvoice(invoice, creditLogs) {
  if (!invoice || !creditLogs?.length) return [];
  return creditLogs.filter(
    (log) =>
      (log.invoice && String(log.invoice) === String(invoice._id)) ||
      (log.invoiceNumber && String(log.invoiceNumber) === String(invoice.invoiceNumber)),
  );
}

export function InvoiceDetailContent({
  invoice,
  creditLogs = [],
  paymentMethods = [],
  mode = "readonly",
  onRegisterPayment,
  onEditPayment,
  onDeletePayment,
}) {
  if (!invoice) return null;

  const logs = filterCreditLogsForInvoice(
    invoice,
    creditLogs?.length ? creditLogs : invoice.client?.creditLogs,
  );

  const payments = invoice.payments || [];

  return (
    <div className="space-y-4 sm:space-y-5">
      <div className="text-center border-b border-slate-100 pb-3">
        <BrandLogo className="w-14 h-14 mx-auto mb-2" alt={COMPANY_NAME} />
        <p className="font-semibold text-gray-900 text-sm">{COMPANY_NAME}</p>
        <p className="text-xs text-gray-500 mt-0.5">RIF {COMPANY_RIF}</p>
      </div>
      <div className="bg-slate-50 rounded-lg p-3 sm:p-4 border border-slate-100">
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Cliente</p>
        <p className="font-semibold text-gray-800 text-sm sm:text-base">
          {invoice.client?.name || "Cliente no encontrado"}
        </p>
        {sellerLabel(invoice.client?.seller) ? (
          <span className="inline-flex mt-2 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
            {sellerLabel(invoice.client?.seller)}
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-200 p-3 bg-white">
          <p className="text-xs uppercase tracking-wide text-gray-500">Total Factura</p>
          <p className="font-semibold mt-1 text-sm sm:text-base">
            {formatCurrency(invoice.totalAmount)}
          </p>
        </div>
        <div className="rounded-lg border border-red-100 p-3 bg-red-50/40">
          <p className="text-xs uppercase tracking-wide text-gray-500">Pendiente</p>
          <p className="font-semibold text-red-500 mt-1 text-sm sm:text-base">
            {formatCurrency(invoice.pendingAmount)}
          </p>
        </div>
      </div>

      {(invoice.creditApplied || 0) > 0 && (
        <div className="rounded-lg border border-emerald-100 p-3 bg-emerald-50/40">
          <p className="text-xs uppercase tracking-wide text-gray-500">Crédito aplicado al generar</p>
          <p className="font-semibold text-emerald-700 mt-1">
            {formatCurrency(invoice.creditApplied)}
          </p>
        </div>
      )}

      {logs.length > 0 && (
        <div className="rounded-lg border border-gray-200 p-3 bg-white">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Logs de crédito aplicados en esta factura
          </p>
          <ul className="space-y-1">
            {[...logs]
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .slice(0, 3)
              .map((log, idx) => (
                <li key={`${log.date}-${idx}`} className="text-sm text-gray-700">
                  {new Date(log.date).toLocaleString("es-VE")} — {log.note || log.action} (
                  {log.delta >= 0 ? "+" : "-"} {formatCurrency(Math.abs(log.delta))})
                </li>
              ))}
          </ul>
        </div>
      )}

      <div className="border-t border-gray-100 pt-4">
        <h4 className="font-semibold mb-2 text-gray-700 text-sm sm:text-base">Ítems de la Factura</h4>
        {invoice.items?.length > 0 ? (
          <ul className="divide-y divide-gray-200 max-h-[min(40vh,280px)] overflow-y-auto bg-white border border-gray-100 rounded-lg px-3">
            {invoice.items.map((item, index) => (
              <li key={index} className="py-2.5">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base">{item.name}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {item.byWeight
                        ? `(x${item.quantity}) ${item.weight}kg @ ${formatCurrency(item.price)}/kg`
                        : `${item.quantity} x ${formatCurrency(item.price)}`}
                    </p>
                  </div>
                  <p className="font-semibold text-sm sm:text-base shrink-0">
                    {formatCurrency(item.total)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-4 text-gray-500 text-sm">No hay ítems en esta factura.</div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-2">
          <h4 className="font-semibold text-gray-700 text-sm sm:text-base">Pagos Registrados</h4>
          {mode === "manage" && onRegisterPayment && invoiceHasPendingBalance(invoice) && (
            <Button
              variant="outline"
              size="sm"
              className="w-full sm:w-auto justify-center"
              onClick={onRegisterPayment}
            >
              <Plus size={16} className="mr-2 shrink-0" />
              Registrar Pago
            </Button>
          )}
        </div>
        {payments.length > 0 ? (
          <ul className="divide-y divide-gray-200 max-h-[min(35vh,240px)] overflow-y-auto rounded-lg border border-gray-100 bg-white px-3">
            {payments.map((payment) => (
              <li key={payment._id || payment.createdAt} className="py-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm sm:text-base">
                      {formatCurrency(payment.amount, payment.currency)}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {payment.date
                        ? formatDate(new Date(payment.date))
                        : payment.createdAt
                          ? formatDate(new Date(payment.createdAt))
                          : "—"}
                    </p>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 flex items-center justify-between sm:justify-end gap-2 shrink-0">
                    <div className="min-w-0 text-right sm:text-left">
                      <p>{resolvePaymentMethodName(payment, paymentMethods)}</p>
                      {payment.currency === "BS" && payment.conversionDetails && (
                        <p className="text-xs text-gray-400">
                          ({formatCurrency(payment.conversionDetails.amountInUSD, "USD")} @{" "}
                          {payment.conversionDetails.exchangeRate})
                        </p>
                      )}
                    </div>
                    {mode === "manage" && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        {onEditPayment && (
                          <button
                            type="button"
                            className="text-indigo-600 hover:text-indigo-800 p-1"
                            title="Editar pago"
                            onClick={() => onEditPayment(payment)}
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        {onDeletePayment && (
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Eliminar pago"
                            onClick={() => onDeletePayment(payment._id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            No hay pagos registrados para esta factura.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Modal unificado para ver / gestionar una factura (mismo diseño en todos los módulos).
 */
export default function InvoiceViewModal({
  open,
  onClose,
  invoice,
  titlePrefix = "Factura",
  mode = "readonly",
  zClass = "z-[70]",
  creditLogs,
  paymentMethods: paymentMethodsProp,
  onEditInvoice,
  onDeleteInvoice,
  onRegisterPayment,
  onEditPayment,
  onDeletePayment,
}) {
  const { shareCardRef, sharing, shareInvoiceImage } = useInvoiceShareImage(open ? invoice : null);
  const [fetchedPaymentMethods, setFetchedPaymentMethods] = useState([]);

  useEffect(() => {
    if (!open || paymentMethodsProp?.length) return undefined;
    let cancelled = false;
    getPaymentMethods()
      .then((res) => {
        if (!cancelled) setFetchedPaymentMethods(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setFetchedPaymentMethods([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, paymentMethodsProp]);

  const paymentMethods = paymentMethodsProp?.length
    ? paymentMethodsProp
    : fetchedPaymentMethods;

  if (!open || !invoice) return null;

  const shareButton = (
    <button
      type="button"
      onClick={shareInvoiceImage}
      disabled={sharing}
      title="Compartir imagen de la factura"
      className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
    >
      {sharing ? <Loader2 size={22} className="animate-spin" /> : <Share2 size={22} />}
    </button>
  );

  const shareFooterBtn = (
    <Button
      variant="outline"
      className="w-full sm:w-auto justify-center"
      onClick={shareInvoiceImage}
      disabled={sharing}
    >
      {sharing ? (
        <Loader2 size={16} className="mr-2 animate-spin shrink-0" />
      ) : (
        <Share2 size={16} className="mr-2 shrink-0" />
      )}
      Compartir imagen
    </Button>
  );

  const footer =
    mode === "manage" ? (
      <AppModalFooter>
        <div className="flex flex-col gap-2 w-full sm:flex-row sm:flex-wrap sm:w-auto">
          {shareFooterBtn}
          {onEditInvoice && (
            <Button variant="outline" className="w-full sm:w-auto justify-center" onClick={onEditInvoice}>
              Editar Factura
            </Button>
          )}
          {onDeleteInvoice && (
            <Button variant="danger" className="w-full sm:w-auto justify-center" onClick={onDeleteInvoice}>
              <Trash2 size={16} className="mr-2 shrink-0" />
              Eliminar Factura
            </Button>
          )}
        </div>
        <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose}>
          Cerrar
        </Button>
      </AppModalFooter>
    ) : (
      <AppModalFooter>
        {shareFooterBtn}
        <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose}>
          Cerrar
        </Button>
      </AppModalFooter>
    );

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <InvoiceShareCard
          ref={shareCardRef}
          invoice={invoice}
          paymentMethods={paymentMethods}
        />
      </div>
      <AppModalShell
        open={open}
        onClose={onClose}
        title={`${titlePrefix} # ${invoice.invoiceNumber}`}
        widthClass="max-w-2xl"
        zClass={zClass}
        headerAction={shareButton}
        footer={footer}
      >
        <InvoiceDetailContent
          invoice={invoice}
          creditLogs={creditLogs}
          paymentMethods={paymentMethods}
          mode={mode}
          onRegisterPayment={onRegisterPayment}
          onEditPayment={onEditPayment}
          onDeletePayment={onDeletePayment}
        />
      </AppModalShell>
    </>
  );
}
